<template>
    <div>
        <label for="localidad">{{ $t('formularios.localidad') }}</label>
        <ValidationProvider name="localidad" id="localidad" rules="required" v-slot="{ errors }">
            <template v-if="locations.length > 0 || forceMultiselect === true">
                <!-- `loading` deshabilita el control y el multi-select muestra su
                     propio spinner: sin esto el usuario podía elegir una localidad
                     de la provincia ANTERIOR mientras llegaba la nueva respuesta. -->
                <multiselect
                    data-cy="localidad"
                    :disabled="desactivar || loading"
                    :loading="loading"
                    v-model="selected"
                    :placeholder="$t('formularios.localidad')"
                    label="nombre"
                    track-by="id"
                    :options="locations"
                    :multiple="false"
                    v-on:select="(selectedOpt) => $emit('selected', selectedOpt)"
                />
            </template>
            <template v-else>
                <input type="text" :class="'form-control ' + (errors[0] ? 'is-invalid' : '')" placeholder="Localidad" v-model="localidadTexto" @change="handleTextInput" :disabled="desactivar || loading" />
            </template>

            <span v-if="loading" class="tw:block tw:text-xs tw:text-gray-500 tw:mt-1" role="status" aria-live="polite">
                {{ 'Cargando localidades…' }}
            </span>

            <span v-if="error" class="tw:block tw:text-xs tw:text-red-600 tw:mt-1" role="alert">
                {{ 'No se han podido cargar las localidades.' }}
            </span>

            <span v-if="errors[0]" class="invalid-feedback mb-4 text-xs" style="font-size: 11px; width: 99.72%; display: block; background-color: #DC3545; color: white; padding: 6px;"><b>{{ errors[0] }}</b></span>
        </ValidationProvider>
    </div>
</template>

<script>
    import { useLocations } from '@/composables/common/useLocations';

    export default {
        props: {
            provincia: {
                type: [Object, Number],
                default: null
            },
            location_id: {
                type: Number,
                default: null
            },
            errors: {
                type: Array,
                default: () => []
            },
            desactivar: {
                type: Boolean,
                default: false
            },
            localidad: {
                type: [String, Object],
                default: null
            },
            forceMultiselect: {
                type: Boolean,
                default: false
            }
        },
        setup() {
            // Carga y ciclo de vida (loading/error + guard de carrera) en el
            // composable; el .vue sólo orquesta la vista y los eventos.
            const { locations, loading, error, cargarLocations, invalidate, applyLocations } = useLocations();

            return { locations, loading, error, cargarLocations, invalidate, applyLocations };
        },
        data() {
            return {
                selected: null,
                localidadTexto: ''
            }
        },
        created() {
            if(this.provincia) {
                if(this.provincia.localidades) {
                    // Vía el composable: pasar a la caché también debe invalidar
                    // cualquier petición en vuelo, o su respuesta tardía repintaría
                    // las localidades de la provincia anterior.
                    this.applyLocations(this.provincia.localidades);
                    this.syncSelected();
                } else {
                    this.getLocations(this.provincia);
                }
            }
        },
        watch: {
            provincia: function(newVal, oldVal) {
                if(newVal === null) {
                    this.invalidate();
                    this.selected = null;
                } else if(newVal !== oldVal) {
                    this.selected = null;
                    if(newVal.localidades) {
                        this.applyLocations(newVal.localidades);
                        this.syncSelected();
                    } else {
                        this.getLocations(newVal);
                    }
                }
            },
            localidad: {
                handler(newVal) {
                    if (newVal && this.locations.length > 0) {
                        this.syncSelectedLocalidad(newVal);
                    }
                },
                immediate: true
            },
        },
        methods: {
            handleTextInput() {
                const localidadManual = {
                    id: null,
                    nombre: this.localidadTexto
                };
                this.selected = localidadManual;
                this.$emit('selected', localidadManual);
            },
            getLocations(provincia) {
                if(provincia == null) {
                    this.cargarLocations(null);
                    return;
                }

                // El composable descarta respuestas fuera de orden, así que cambiar
                // de provincia dos veces rápido ya no sobrescribe con datos viejos.
                this.cargarLocations(provincia).then(locations => {
                    if (!locations.length) {
                        return;
                    }

                    this.syncSelected();

                    this.$emit('localidadesCargadas', locations);
                });
            },
            syncSelectedLocalidad(localidadNombre) {
                if (!localidadNombre || !this.locations.length) {
                    return;
                }
                
                const localidadEncontrada = this.locations.find(location => {
                    if (typeof localidadNombre === 'string') {
                        return location.nombre === localidadNombre;
                    }
                    if (typeof localidadNombre === 'object') {
                        return location.id === localidadNombre.id || location.nombre === localidadNombre.nombre;
                    }                    
                });
                
                if (localidadEncontrada) {
                    this.selected = localidadEncontrada;
                    this.$emit('selected', localidadEncontrada);
                } 
            },
            syncSelectedLocalidadById(locationId) {                
                const localidadEncontrada = this.locations.find(location => location.id === locationId);
                
                if (localidadEncontrada) {
                    this.selected = localidadEncontrada;
                    this.$emit('selected', localidadEncontrada);
                } else {
                    this.selected = null;
                    this.$emit('selected', null);
                }
            },
            syncSelected() {
                if (this.localidad) {
                    if (typeof this.localidad === 'string') {
                        this.localidadTexto = this.localidad;
                    }
                    this.syncSelectedLocalidad(this.localidad);
                }

                if (this.location_id && !this.selected) {
                    this.syncSelectedLocalidadById(this.location_id);
                }
            }
        },
    }
</script>
